import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

export type ChallengeIdeaFiltersState = {
  query: string;
  tags: string[];
  status: "submitted" | "draft" | "all";
  authorGroupIds: string[];
  labels: string[];
  authorOrganisationIds: string[];
  sort: string;
  sortDirection: "asc" | "desc";
  page: number;
  limit: number;
  fieldChoices: string[];
  assignees: ("me" | "unassigned" | string)[];
  lanes: string[];
  ideaViewType: "card" | "list" | "board";
  filtersVisible: boolean;
  hasAdminComment: "yes" | "no" | "all";
  hasCustomDeadline: "yes" | "no" | "all";
  impactIds: string[];
  statusFilter: string[];
};

type ChallengeIdeaFiltersStore = ChallengeIdeaFiltersState & {
  setIdeaViewType: (
    ideaViewType: ChallengeIdeaFiltersState["ideaViewType"],
  ) => void;
};

export const initialChallengeIdeaFiltersState: ChallengeIdeaFiltersState = {
  query: "",
  tags: [],
  status: "all",
  authorGroupIds: [],
  labels: [],
  authorOrganisationIds: [],
  sort: "default",
  sortDirection: "desc",
  page: 1,
  limit: 16,
  fieldChoices: [],
  lanes: [],
  ideaViewType: "card",
  filtersVisible: false,
  hasAdminComment: "all",
  hasCustomDeadline: "all",
  assignees: [],
  impactIds: [],
  statusFilter: [],
};

export const useChallengeIdeaFiltersStore = create<ChallengeIdeaFiltersStore>(
  (set) => ({
    ...initialChallengeIdeaFiltersState,
    setIdeaViewType: (ideaViewType) =>
      set((state) => {
        // Opening an idea on the board re-applies this via search params;
        // keep existing filters unless the view type actually changed.
        if (ideaViewType === state.ideaViewType) return state;
        return {
          ideaViewType,
          limit: ideaViewType === "card" ? 16 : 30,
          status:
            ideaViewType === "board"
              ? "submitted"
              : state.ideaViewType === "board"
                ? "all"
                : state.status,
        };
      }),
  }),
);

export const useChallengeIdeaFilters = (): ChallengeIdeaFiltersState =>
  useChallengeIdeaFiltersStore(
    useShallow(({ setIdeaViewType: _setIdeaViewType, ...filters }) => filters),
  );
